"use client";

import posthog from "posthog-js";
import * as Sentry from "@sentry/react";

let initialized = false;

export type ExcelEvent =
  | "excel.signed_in"
  | "excel.signed_out"
  | "excel.session_started"
  | "excel.message_sent"
  | "excel.message_received"
  | "excel.action_applied"
  | "excel.action_dismissed"
  | "excel.pin_set"
  | "excel.pin_cleared"
  | "excel.tenant_picked"
  | "excel.host_theme_mismatch"
  | "excel.error";

/**
 * Initialize PostHog. Sentry is initialized separately in src/lib/sentry.ts
 * and called from each entry's bootstrap BEFORE render so it can catch
 * render-time errors. PostHog can wait for component mount — it's just
 * event tracking, not error capture.
 */
export function initTelemetry() {
  if (initialized) return;
  if (typeof window === "undefined") return;

  const phKey = import.meta.env.VITE_POSTHOG_KEY;
  const phHost = import.meta.env.VITE_POSTHOG_HOST;
  if (phKey && phHost) {
    posthog.init(phKey, {
      api_host: phHost,
      capture_pageview: false,
      autocapture: false,
      persistence: "localStorage",
      loaded: (ph) => {
        ph.register({
          client: "excel-addin",
          client_version: import.meta.env.VITE_VERSION ?? "0.1.0",
        });
      },
    });
  }

  initialized = true;
}

export function track(event: ExcelEvent, props: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  posthog.capture(event, props);
}

export function identify(mirrorId: string) {
  if (typeof window === "undefined") return;
  posthog.identify(mirrorId);
}

export function captureError(err: unknown, context: Record<string, unknown> = {}) {
  Sentry.captureException(err, { extra: context });
  track("excel.error", {
    code: (err as { code?: string })?.code,
    message: (err as { message?: string })?.message,
    ...context,
  });
}
